<template lang="pug">
.insights-start-stop-time-widget.insights-widget(
  v-if="showRvoWidgets"
)
  .widget-wrapper
    .widget-common.widget-small.widget-full-height
      .widget-title.d-flex.align-items-center.justify-content-between
        .d-flex.align-items-baseline
          | {{ title }}
          i.hi.hi-info-filled.ml-5(
            v-tooltip="{ placement: 'top' }"
            title="All start and stop times are shown in the member's time zone. To view them in your organization's time zone, go to the Timeline."
          )

      .widget-body(:class="bodyClasses")
        template(v-if="isReady")
          template(v-if="!isEmptyWidget")
            .start-stop-container(ref="container")
              InsightsStartStopTimeLinesOverlay(
                :label-count="scaleLabels.length"
                :remote-vs-office="remoteVsOffice"
              )

              InsightsStartStopTimeScaleHeader(
                :labels="scaleLabels"
                :remote-vs-office="remoteVsOffice"
                :is-narrow="isNarrow"
              )

              .start-stop-divider

              .start-stop-rows
                template(v-if="remoteVsOffice")
                  InsightsStartStopTimeRow(
                    v-if="hasRemoteData"
                    :start-time="remoteStart"
                    :stop-time="remoteStop"
                    :scale-start="scaleStart"
                    :scale-end="scaleEnd"
                    :scale-label-count="scaleLabels.length"
                    row-class="start-stop-row-remote"
                    label="Remote"
                    icon="house"
                  )

                  .start-stop-divider(v-if="hasRemoteData && hasOfficeData")

                  InsightsStartStopTimeRow(
                    v-if="hasOfficeData"
                    :start-time="officeStart"
                    :stop-time="officeStop"
                    :scale-start="scaleStart"
                    :scale-end="scaleEnd"
                    :scale-label-count="scaleLabels.length"
                    row-class="start-stop-row-office"
                    label="Office"
                    icon="corporate_fare"
                  )

                  .start-stop-divider

                template(v-else)
                  InsightsStartStopTimeRow(
                    v-if="hasStandardData"
                    :start-time="standardStart"
                    :stop-time="standardStop"
                    :scale-start="scaleStart"
                    :scale-end="scaleEnd"
                    :scale-label-count="scaleLabels.length"
                    row-class="start-stop-row-standard"
                  )

          InsightsNoData(
            v-else
            :type="emptyReason"
          )

      .widget-footer-bordered(v-if="showWidgetBenchmarks")
        InsightsBenchmarksAverages(
          :averages="benchmarks"
          :pins="benchmarkPins"
          :is-loading="!isReady"
          :widget-name="title"
          :min="benchmarkScaleMin"
          :max="benchmarkScaleMax"
          :value-formatter="formatTime"
        )
</template>

<script>
import { mapGetters } from 'vuex';
import { Insights, InsightsTracking } from '~/mixins';
import InsightsBenchmarksAverages from '~/components/insights/InsightsBenchmarksAverages.vue';
import InsightsStartStopTimeRow from '~/components/insights/InsightsStartStopTimeRow.vue';
import InsightsStartStopTimeLinesOverlay from '~/components/insights/InsightsStartStopTimeLinesOverlay.vue';
import InsightsStartStopTimeScaleHeader from '~/components/insights/InsightsStartStopTimeScaleHeader.vue';
import formatMinutesAsTime from '~/components/insights/utils/formatMinutesAsTime';
import { INSIGHTS } from '~/app_constants';

const BENCHMARK_SCALE_MIN_MINUTES = 240;
const BENCHMARK_SCALE_MAX_MINUTES = 1680;

// Time conversion constants
const MINUTES_PER_HOUR = 60;

// Scale calculation constants
const DEFAULT_SCALE_START_MINUTES = 360; // 6:00 AM
const DEFAULT_SCALE_END_MINUTES = 1200; // 8:00 PM
const SCALE_START_BUFFER_MINUTES = 180; // 3 hours buffer before the earliest start time
const EVEN_HOUR_INTERVAL_MINUTES = 120; // 2 hours for rounding to even hours
const SCALE_END_BUFFER_MINUTES = 120; // 2 hours buffer after latest stop time

// Scale step/interval constants
const LARGE_RANGE_THRESHOLD_MINUTES = 960; // 16 hours - threshold for using larger intervals
const LARGE_STEP_INTERVAL_MINUTES = 240; // 4 hours - used for ranges > 16 hours
const DEFAULT_STEP_INTERVAL_MINUTES = 120; // 2 hours - used for smaller ranges

// Layout constants
const NARROW_CONTAINER_WIDTH_THRESHOLD = 500; // px - hide alternate labels below this width

export default {
  name: 'InsightsStartStopTimeWidget',
  components: {
    InsightsBenchmarksAverages,
    InsightsStartStopTimeRow,
    InsightsStartStopTimeLinesOverlay,
    InsightsStartStopTimeScaleHeader,
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
  },
  mixins: [Insights, InsightsTracking],
  data() {
    return {
      widgetKey: INSIGHTS.api.startStopTime,
      resizeObserver: null,
      containerWidth: 0,
      benchmarkScaleMin: BENCHMARK_SCALE_MIN_MINUTES,
      benchmarkScaleMax: BENCHMARK_SCALE_MAX_MINUTES - 1, // Subtract 1 so max label displays 3:59 AM instead of 4:00 AM
    };
  },
  computed: {
    ...mapGetters({
      widgetData: 'startStopTime',
      remoteVsOffice: 'remoteVsOffice',
      showRvoWidgets: 'showRvoWidgets',
      showWidgetBenchmarks: 'showWidgetBenchmarks',
    }),
    title() {
      return 'Start / Stop Times';
    },
    isNarrow() {
      // Hide alternate labels when container is narrower than threshold
      return this.containerWidth > 0 && this.containerWidth < NARROW_CONTAINER_WIDTH_THRESHOLD;
    },
    remoteStart() {
      return this.widgetData.remote?.start_time;
    },
    remoteStop() {
      return this.widgetData.remote?.stop_time;
    },
    officeStart() {
      return this.widgetData.on_site?.start_time;
    },
    officeStop() {
      return this.widgetData.on_site?.stop_time;
    },
    hasRemoteData() {
      return this.remoteStart != null && this.remoteStop != null;
    },
    hasOfficeData() {
      return this.officeStart != null && this.officeStop != null;
    },
    standardStart() {
      return this.widgetData.start_time;
    },
    standardStop() {
      return this.widgetData.stop_time;
    },
    hasStandardData() {
      return this.standardStart != null && this.standardStop != null;
    },
    scaleStart() {
      // Round down min start time to give space for label
      // e.g., 8:59 -> 6:00, 9:00 -> 6:00, 11:00 -> 8:00 (round up to hour, subtract 3 hours, then round down to even hour)
      const times = [this.remoteStart, this.officeStart, this.standardStart].filter((t) => t != null);

      if (!times.length) {
        return DEFAULT_SCALE_START_MINUTES;
      }
      const minStart = Math.min(...times);
      // Round up to next hour first, then subtract buffer for label space, then round down to even hour
      const ceilHour = Math.ceil(minStart / MINUTES_PER_HOUR) * MINUTES_PER_HOUR;
      const withBuffer = ceilHour - SCALE_START_BUFFER_MINUTES;
      const evenHour = Math.floor(withBuffer / EVEN_HOUR_INTERVAL_MINUTES) * EVEN_HOUR_INTERVAL_MINUTES;

      return Math.max(0, evenHour);
    },
    scaleEnd() {
      // Round up max end time to next even hour
      // e.g., 5:30 -> 8:00, 6:00 -> 8:00, 6:30 -> 10:00
      const times = [this.remoteStop, this.officeStop, this.standardStop].filter((t) => t != null);

      if (!times.length) {
        return DEFAULT_SCALE_END_MINUTES;
      }
      const maxEnd = Math.max(...times);
      const evenHour =
        Math.ceil((maxEnd + SCALE_END_BUFFER_MINUTES) / EVEN_HOUR_INTERVAL_MINUTES) * EVEN_HOUR_INTERVAL_MINUTES;

      return evenHour;
    },
    scaleStep() {
      const range = this.scaleEnd - this.scaleStart;

      // Pick the smallest step that keeps labels from overlapping (~7 labels max)
      if (range > LARGE_RANGE_THRESHOLD_MINUTES) {
        return LARGE_STEP_INTERVAL_MINUTES;
      }

      return DEFAULT_STEP_INTERVAL_MINUTES;
    },
    scaleLabels() {
      const labels = [];
      let prevAmPm = null;

      for (let m = this.scaleStart; m <= this.scaleEnd; m += this.scaleStep) {
        const h = Math.floor(m / MINUTES_PER_HOUR);
        const ampm = h >= 12 ? 'pm' : 'am';
        const isFirstLabel = labels.length === 0;
        const isTransition = prevAmPm !== null && prevAmPm !== ampm;
        const showSuffix = isFirstLabel || isTransition;

        labels.push(formatMinutesAsTime(m, { showSuffix }));
        prevAmPm = ampm;
      }

      return labels;
    },
    benchmarks() {
      if (this.isEmptyWidget) {
        return {};
      }

      const all = this.widgetData.benchmarks || {};
      const activeKeys = Object.keys(this.benchmarkValue);

      // Only show benchmark bars for locations that have actual data
      return Object.fromEntries(Object.entries(all).filter(([key]) => activeKeys.includes(key)));
    },
    benchmarkValue() {
      if (this.isEmptyWidget) {
        return {};
      }

      const val = {};

      if (this.remoteStart != null && this.remoteStop != null) {
        val.remote = [this.remoteStart, this.remoteStop];
      }
      if (this.officeStart != null && this.officeStop != null) {
        val.on_site = [this.officeStart, this.officeStop];
      }

      return val;
    },
    benchmarkPins() {
      return Object.entries(this.benchmarkValue).map(([key, value]) => ({ key, value }));
    },
  },
  watch: {
    isReady(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updateContainerWidth();
          if (this.$refs.container && this.resizeObserver) {
            this.resizeObserver.observe(this.$refs.container);
          }
        });
      }
    },
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateContainerWidth();
    });
    this.$nextTick(() => {
      this.updateContainerWidth();
      if (this.$refs.container) {
        this.resizeObserver.observe(this.$refs.container);
      }
    });
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    formatTime: formatMinutesAsTime,
    updateContainerWidth() {
      if (this.$refs.container) {
        this.containerWidth = this.$refs.container.getBoundingClientRect().width;
      }
    },
  },
};
</script>
